import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'

import type { ApiErrorResponse, ApiResponse } from '@/types/api.types'
import type { RefreshTokenResponse } from '@/types/auth.types'
import { tokenManager } from '@/utils/tokenManager'

// Configuration
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY = 1000 // 1 second

// Create base axios instance
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5050',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  return instance
}

// Helper to check if error is network related

const isNetworkError = (error: AxiosError): boolean => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED')
}

// Helper to check if error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  if (isNetworkError(error)) return true
  if (error.response?.status && error.response.status >= 500) return true
  if (error.response?.status === 429) return true // Rate limiting
  return false
}

// Sleep helper for retry delay
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

// Refresh token function
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: AxiosError) => void
}> = []

const processQueue = (error: AxiosError | null, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else if (token) {
      resolve(token)
    }
  })

  failedQueue = []
}

const refreshAuthToken = async (): Promise<string> => {
  // Rama Entra ID: delegar el refresh a MSAL
  if (tokenManager.getAuthMethod() === 'entra') {
    const { acquireEntraAccessToken } = await import('./api/entraAuth.service')
    try {
      return await acquireEntraAccessToken()
    } catch (error) {
      tokenManager.clearAll()
      window.location.href = '/login'
      throw error
    }
  }

  // Rama JWT propio
  const refreshToken = tokenManager.getRefreshToken()

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const response = await axios.post<ApiResponse<RefreshTokenResponse>>(
      `${import.meta.env.VITE_API_URL || 'http://localhost:5050'}/api/v1/auth/refresh`,
      { refresh_token: refreshToken }
    )

    if (response.data.success && response.data.data) {
      const tokens = {
        token: response.data.data.token,
        refresh_token: response.data.data.refresh_token
      }

      tokenManager.setTokens(tokens)
      return tokens.token
    } else {
      throw new Error('Failed to refresh token')
    }
  } catch (error) {
    tokenManager.clearAll()
    window.location.href = '/login'
    throw error
  }
}

// Create API client with interceptors
export const createApiClient = (): AxiosInstance => {
  const client = createAxiosInstance()

  // Request interceptor to add auth token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (!config.headers.Authorization) {
        let token: string | null = null

        if (tokenManager.getAuthMethod() === 'entra') {
          try {
            const { acquireEntraAccessToken } = await import('./api/entraAuth.service')
            token = await acquireEntraAccessToken()
          } catch {
            // MSAL no puede obtener token — el interceptor de response manejará el 401
          }
        } else {
          token = tokenManager.getToken()
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }

      if (import.meta.env.DEV) {
        console.warn(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
          headers: config.headers,
          data: config.data
        })
      }

      return config
    },
    (error) => {
      if (import.meta.env.DEV) {
        console.error('[API Request Error]', error)
      }
      return Promise.reject(error)
    }
  )

  // Response interceptor for token refresh and error handling
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      // Log response in development
      if (import.meta.env.DEV) {
        console.warn(
          `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
          {
            status: response.status,
            data: response.data
          }
        )
      }

      // Note: Not auto-transforming response to preserve original structure for auth services

      return response
    },
    async (error) => {
      const originalRequest = error.config

      // Log error in development
      if (import.meta.env.DEV) {
        console.error(
          `[API Error] ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
          {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          }
        )
      }

      // Handle 401 errors with token refresh — skip auth endpoints (login, refresh)
      const isAuthEndpoint =
        originalRequest?.url?.includes('/auth/login') ||
        originalRequest?.url?.includes('/auth/refresh')
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return client(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const newToken = await refreshAuthToken()
          processQueue(null, newToken)

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return client(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError as AxiosError, null)
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      // Handle retryable errors with exponential backoff
      if (isRetryableError(error) && !originalRequest._retryCount) {
        originalRequest._retryCount = 0
      }

      if (isRetryableError(error) && originalRequest._retryCount < MAX_RETRY_ATTEMPTS) {
        originalRequest._retryCount += 1
        const delay = RETRY_DELAY * Math.pow(2, originalRequest._retryCount - 1)

        if (import.meta.env.DEV) {
          console.warn(
            `[API Retry] Attempt ${originalRequest._retryCount}/${MAX_RETRY_ATTEMPTS} after ${delay}ms`
          )
        }

        await sleep(delay)
        return client(originalRequest)
      }

      // Transform error response only if it doesn't already have a proper structure
      // Preserve backend error structure with 'detail' field
      if (error.response?.data?.detail || error.response?.data?.data?.detail) {
        // Backend error already has proper structure, don't transform
        return Promise.reject(error)
      }

      // Transform error response for other types of errors
      const apiError: ApiErrorResponse = {
        success: false,
        data: null,
        errors: error.response?.data?.errors || [
          {
            code: error.code || 'UNKNOWN_ERROR',
            message: error.message || 'An unknown error occurred'
          }
        ]
      }

      return Promise.reject({
        ...error,
        response: {
          ...error.response,
          data: apiError
        }
      })
    }
  )

  return client
}

// Default API client instance
export const apiClient = createApiClient()

// Helper function to check if token needs refresh before making requests
export const ensureValidToken = async (): Promise<void> => {
  // Para Entra ID el interceptor de request adquiere el token fresco automáticamente.
  if (tokenManager.getAuthMethod() === 'entra') return

  if (tokenManager.shouldRefreshToken() && !isRefreshing) {
    try {
      await refreshAuthToken()
    } catch (error) {
      throw 'Token refresh failed, please log in again: ' + error
    }
  }
}

// Export types for use in other services
export type { ApiErrorResponse, ApiResponse } from '@/types/api.types'
